import { useMemo } from "react";
import type { MatchTeamPublic } from "@dugads/shared";

interface ParticipatingTeamsSectionProps {
  matchTeams: Record<string, MatchTeamPublic[]>;
}

interface SchoolTeamGroup {
  school_name: string;
  school_logo_url: string | null;
  school_primary_color: string | null;
  teams: { team_id: string; team_name: string; age_group: string }[];
}

export function ParticipatingTeamsSection({ matchTeams }: ParticipatingTeamsSectionProps) {
  const groups = useMemo<SchoolTeamGroup[]>(() => {
    const seen = new Set<string>();
    const bySchool = new Map<string, SchoolTeamGroup>();

    for (const teams of Object.values(matchTeams)) {
      for (const t of teams) {
        if (seen.has(t.team_id)) continue;
        seen.add(t.team_id);

        const schoolKey = t.school_name ?? "Unknown";
        if (!bySchool.has(schoolKey)) {
          bySchool.set(schoolKey, {
            school_name: schoolKey,
            school_logo_url: t.school_logo_url,
            school_primary_color: t.school_primary_color,
            teams: [],
          });
        }
        bySchool.get(schoolKey)!.teams.push({
          team_id: t.team_id,
          team_name: t.team_name,
          age_group: t.age_group,
        });
      }
    }

    return [...bySchool.values()].sort((a, b) => a.school_name.localeCompare(b.school_name));
  }, [matchTeams]);

  if (groups.length === 0) return null;

  return (
    <div className="mt-3 flex flex-wrap items-start gap-4" data-region="participating-teams">
      {groups.map((group) => (
        <div key={group.school_name} className="flex items-start gap-2">
          {/* School crest */}
          {group.school_logo_url ? (
            <img
              src={group.school_logo_url}
              alt={group.school_name}
              className="h-7 w-7 shrink-0 rounded-sm object-contain"
            />
          ) : (
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-sm text-[10px] font-bold text-white"
              style={{ background: group.school_primary_color ?? "var(--color-primary)" }}
              aria-hidden="true"
            >
              {group.school_name.charAt(0).toUpperCase()}
            </span>
          )}

          <div className="flex flex-col gap-0.5">
            <span className="text-xs font-semibold text-foreground leading-tight">{group.school_name}</span>
            <div className="flex flex-wrap gap-1">
              {group.teams.map((t) => (
                <span
                  key={t.team_id}
                  className="inline-flex items-center rounded-sm bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground leading-tight"
                >
                  {t.team_name}
                  {t.age_group ? <span className="ml-1 text-[10px] opacity-70">{t.age_group}</span> : null}
                </span>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
